import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { downloadBlobUrlFileToBrowser } from '@highlander/common/helpers/file.helpers';
import { FileListItem, FileResponse } from '@models/index';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { createCustomStateSetOnUrlHandler, createStateResetHandler, deleteEntity, getAll, getOne, StateDictionary } from '../../helpers';
import { FilesService } from './files.service';

type FilesState = {
  files: FileListItem[];
  isLoading: boolean;
  selectedFile: FileResponse | null;
  selectedFileLoading: boolean;
};

const initialState: FilesState = {
  files: [],
  isLoading: false,
  selectedFile: null,
  selectedFileLoading: false,
};

const filesDictionary: StateDictionary<FileListItem> = {
  entityList: 'files',
  listLoading: 'isLoading',
  selectedEntity: 'selectedFile',
  selectedEntityLoading: 'selectedFileLoading',
  entityId: 'id',
  sortFn: (a: FileListItem, b: FileListItem) => a.fileName.localeCompare(b.fileName),
  //unused
  createLoading: '',
};

export const FilesStore = signalStore(
  withState(initialState),
  withDevtools('files-store'),
  withComputed(store => ({
    selectedFileId: computed(() => store.selectedFile()?.id ?? null),
  })),
  withMethods((store, filesService = inject(FilesService), router = inject(Router)) => {
    createStateResetHandler<FilesState>('/file-management/files')(store, initialState);
    createCustomStateSetOnUrlHandler<FilesState>('/file-management/files', { selectedFile: null })(store);

    const repositoryMethods = {
      getAll: getAll<FileListItem>({
        store,
        service: filesService,
        dictionary: filesDictionary,
      }),

      getOne: getOne<FileResponse>({
        store,
        service: filesService,
        dictionary: filesDictionary,
      }),

      deleteFile: deleteEntity<FileListItem>({
        store,
        service: filesService,
        dictionary: filesDictionary,
        onSuccessCallback: () => {
          patchState(store, { selectedFile: null });
          router.navigate(['file-management', 'files']);
          repositoryMethods.getAll();
        },
      }),

      downloadFile: rxMethod<string>(
        pipe(
          switchMap(id =>
            filesService.getDownloadUrl(id).pipe(
              tap(url => {
                downloadBlobUrlFileToBrowser(url.URL);
              }),
              catchError(() => {
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };

    const navigationActions = {
      goToFileActions: async (id: string): Promise<boolean> => {
        const result = await router.navigate(['file-management', 'files', id]);
        if (!result) return false;

        if (!store.selectedFileLoading()) repositoryMethods.getOne(id);
        return true;
      },

      goToListPage: (): void => {
        patchState(store, { selectedFile: null });
        router.navigate(['file-management', 'files']);
      },

      resetState: (state: Partial<FilesState>): void => patchState(store, state),
    };

    return {
      ...repositoryMethods,
      ...navigationActions,
    };
  }),
);

export interface FilesStoreType {
  readonly files: Signal<FileListItem[]>;
  readonly isLoading: Signal<boolean>;
  readonly selectedFileLoading: Signal<boolean>;
  readonly selectedFile: Signal<FileResponse | null>;

  readonly selectedFileId: Signal<string | null>;

  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly deleteFile: (id: string) => void;
  readonly downloadFile: (id: string) => void;
  readonly goToFileActions: (id: string) => Promise<boolean>;
  readonly goToListPage: () => void;
  readonly resetState: (state: Partial<FilesState>) => void;
}
